import type {BrowseCourse} from '@udemy-v2/browse-course';
import type {TrackableDiscoveryItem} from '@udemy-v2/react-browse-event-tracking';
import {
    RecommendationContextSection,
    RecommendationSourceObjectType,
} from '@udemy-v2/react-recommendations';

export const getContextInput = (labelId: number) => ({
    contextId: 'topic',
    sourceObjects: [{id: String(labelId), type: RecommendationSourceObjectType.Label}],
    requestedSectionCount: 1,
    requestedItemCountPerUnit: 5,
});

export interface PsuTab {
    name: string;
    labelId: number;
    topicUrl: string;
}

// A PsuTab before its display name is resolved from the enrollment-stats API.
export interface PsuTabRef {
    labelId: number;
    topicUrl: string;
}

interface PsuTopic {
    labelId: number;
    // Only set when the topic page slug differs from the key (e.g. `excel` -> `/topic/microsoft-excel/`).
    topicUrl?: string;
}

// Names are resolved at runtime by usePsuTabs() via the enrollment-stats API, not stored here.
const PSU_TOPICS: Record<string, PsuTopic> = {
    'artificial-intelligence': {labelId: 4624},
    python: {labelId: 7380},
    excel: {labelId: 6730, topicUrl: '/topic/microsoft-excel/'},
    'ai-agents': {labelId: 583042},
    'digital-marketing': {labelId: 5430},
    'amazon-aws': {labelId: 4452},
    n8n: {labelId: 721407},
    'english-language': {labelId: 5626},
    'web-development': {labelId: 8322},
    'microsoft-power-bi': {labelId: 6740},
    chatgpt: {labelId: 158002},
    'project-management': {labelId: 7336},
    'data-science': {labelId: 5336},
    javascript: {labelId: 6368},
    'project-planning': {labelId: 17908},
    'social-media-marketing': {labelId: 7788},
    'generative-ai': {labelId: 158078},
    'aws-certified-cloud-practitioner': {labelId: 46868},
    'information-technology-engineers-exam': {labelId: 157986},
    'unreal-engine': {labelId: 8166},
    langchain: {labelId: 2146122},
    midjourney: {labelId: 158096},
    wordpress: {labelId: 8452},
    'dutch-language': {labelId: 5516},
    'personal-finance': {labelId: 7140},
    sql: {labelId: 7862},
    java: {labelId: 6336},
    'software-testing': {labelId: 7812},
    photoshop: {labelId: 4402},
    math: {labelId: 6646},
    'affiliate-marketing': {labelId: 4416},
    'italian-language': {labelId: 6330},
    'c-plus-plus': {labelId: 4908},
    figma: {labelId: 46554},
    'video-editing': {labelId: 8210},
    'prompt-engineering': {labelId: 158142},
    'rust-programming-language': {labelId: 25978},
    react: {labelId: 7450},
    'cisco-ccna': {labelId: 5044},
    'aws-certified-solutions-architect-associate': {labelId: 24774},
    'human-resources': {labelId: 6154},
    'machine-learning': {labelId: 6594},
    'front-end-web-development': {labelId: 5884},
    'data-analysis': {labelId: 5328},
    odoo: {labelId: 6970},
    'business-strategy': {labelId: 4898},
};

// Keyed by language code, not full locale (see resolveLocaleLang below).
const PSU_TOPICS_BY_LOCALE: Record<string, string[]> = {
    en: [
        'artificial-intelligence',
        'python',
        'excel',
        'ai-agents',
        'digital-marketing',
        'amazon-aws',
    ],
    es: [
        'artificial-intelligence',
        'python',
        'excel',
        'n8n',
        'english-language',
        'digital-marketing',
    ],
    pt: [
        'artificial-intelligence',
        'excel',
        'python',
        'microsoft-power-bi',
        'english-language',
        'digital-marketing',
    ],
    de: ['artificial-intelligence', 'python', 'excel', 'ai-agents', 'digital-marketing', 'chatgpt'],
    fr: ['n8n', 'python', 'excel', 'microsoft-power-bi', 'chatgpt', 'project-management'],
    id: ['python', 'digital-marketing', 'data-science', 'excel', 'javascript', 'project-planning'],
    it: [
        'chatgpt',
        'excel',
        'python',
        'english-language',
        'social-media-marketing',
        'artificial-intelligence',
    ],
    ja: [
        'generative-ai',
        'python',
        'chatgpt',
        'aws-certified-cloud-practitioner',
        'web-development',
        'information-technology-engineers-exam',
    ],
    ko: [
        'chatgpt',
        'python',
        'unreal-engine',
        'langchain',
        'artificial-intelligence',
        'web-development',
    ],
    nl: ['midjourney', 'excel', 'python', 'wordpress', 'dutch-language', 'personal-finance'],
    pl: ['chatgpt', 'sql', 'web-development', 'java', 'software-testing', 'microsoft-power-bi'],
    ro: ['excel', 'photoshop', 'math', 'affiliate-marketing', 'italian-language', 'c-plus-plus'],
    ru: ['chatgpt', 'figma', 'web-development', 'sql', 'software-testing', 'video-editing'],
    th: [
        'prompt-engineering',
        'python',
        'rust-programming-language',
        'web-development',
        'react',
        'cisco-ccna',
    ],
    tr: ['chatgpt', 'english-language', 'python', 'excel', 'data-science', 'web-development'],
    vi: [
        'chatgpt',
        'python',
        'project-management',
        'amazon-aws',
        'aws-certified-solutions-architect-associate',
        'human-resources',
    ],
    zh: [
        'generative-ai',
        'python',
        'machine-learning',
        'chatgpt',
        'front-end-web-development',
        'data-analysis',
    ],
    ar: [
        'artificial-intelligence',
        'excel',
        'odoo',
        'project-management',
        'business-strategy',
        'digital-marketing',
    ],
};

// e.g. `ja_JP` -> `ja`; both `zh_CN`/`zh_TW` collapse to the single `zh` bucket.
const resolveLocaleLang = (locale: string) => locale.split('_')[0].toLowerCase();

export const getPsuTabsForLocale = (locale: string): PsuTabRef[] => {
    const lang = resolveLocaleLang(locale);
    const topicSlugs = PSU_TOPICS_BY_LOCALE[lang] ?? PSU_TOPICS_BY_LOCALE.en;

    return topicSlugs.map((slug) => {
        const topic = PSU_TOPICS[slug];
        return {
            labelId: topic.labelId,
            topicUrl: topic.topicUrl ?? `/topic/${slug}/`,
        };
    });
};

export const extractCourses = (
    data: RecommendationContextSection[],
): TrackableDiscoveryItem<BrowseCourse>[] => {
    const [group] = data;

    const hasData = group && 'units' in group;
    if (!hasData) return [];

    const courses: TrackableDiscoveryItem<BrowseCourse>[] = [];

    for (const unit of group.units) {
        for (const item of unit.items) {
            if (item.course) {
                courses.push({
                    ...item.course,
                    trackingId: item.trackingId,
                    frontendTrackingId: item.frontendTrackingId,
                } as TrackableDiscoveryItem<BrowseCourse>);
            }
        }
    }

    return courses;
};
